import React from "react";
import {
    Box,
    Button,
    Checkbox,
    ListItem,
    ListItemButton,
    ListItemIcon,
    TextField,
    Typography,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import styled from "styled-components";
import { ActiveDate } from "src/scenes/dashboard/date-provider";
import { useSprints } from "src/scenes/dashboard/selected-sprint-provider";
import { useAuth } from "src/components/providers/auth-context";
import { format, isAfter, subMonths } from "date-fns";
import List from "@mui/material/List";
import { useTeamSprints } from "src/scenes/teams/dashboard/selected-sprints-provider-teams";

const DateText = styled.p`
    font-size: 10px;
    margin: 0;
    opacity: 0.6;
`;

const SprintPickerModal = ({ handleClose, isTeams }) => {
    const [searchQuery, setSearchQuery] = React.useState("");
    const { handleChange } = React.useContext(ActiveDate);
    const { isDemo } = useAuth();
    const { allSprints, handleSprintChange, selectedSprints } = isTeams
        ? useTeamSprints()
        : useSprints();
    const { isUserPayingCustomer } = useAuth();
    const [sprintData, setSprintData] = React.useState([]);
    const [pendingSelection, setPendingSelection] = React.useState(selectedSprints ?? []);

    React.useEffect(() => {
        setPendingSelection(selectedSprints ?? []);
    }, [selectedSprints]);

    const handleSearchInput = (event) => {
        setSearchQuery(event.target.value || null);
    };

    React.useEffect(() => {
        let tempData = [...allSprints];
        if (searchQuery)
            tempData = allSprints.filter((s) =>
                s.name.toLowerCase().includes(searchQuery.toLowerCase())
            );
        setSprintData(tempData);
    }, [searchQuery, allSprints]);

    const isSelected = (sprint) => pendingSelection.some((s) => s.id === sprint.id);

    const toggleSprint = (sprint) => {
        setPendingSelection((prev) =>
            isSelected(sprint) ? prev.filter((s) => s.id !== sprint.id) : [...prev, sprint]
        );
    };

    const applySelection = () => {
        if (pendingSelection.length === 0) return;
        handleSprintChange(pendingSelection);
        const sorted = [...pendingSelection].sort(
            (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
        );
        handleChange(new Date(sorted[0].startDate), new Date(sorted[sorted.length - 1].endDate));
        handleClose();
    };

    const isDisabled = (option) =>
        !isUserPayingCustomer &&
        !isDemo &&
        isAfter(new Date(subMonths(new Date(), 3).toISOString()), new Date(option.startDate));

    if (!sprintData) return null;

    return (
        <Stack direction="column" padding={2} style={{ maxHeight: "50vh" }}>
            <p>Select sprints</p>
            <TextField
                onChange={handleSearchInput}
                type="search"
                id="search-sprints"
                size="small"
                label={
                    <Typography sx={{ fontSize: 12, paddingTop: "3px" }}>Search Sprints</Typography>
                }
                variant="outlined"
            />
            <Box
                sx={{
                    width: "100%",
                    height: "300px",
                    overflowX: "auto",
                    paddingBottom: "12px",
                    marginTop: "12px",
                }}
            >
                <List dense>
                    {sprintData.map((option) => (
                        <ListItemButton
                            key={option.id}
                            disabled={isDisabled(option)}
                            onClick={() => toggleSprint(option)}
                        >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                                <Checkbox
                                    edge="start"
                                    checked={isSelected(option)}
                                    tabIndex={-1}
                                    disableRipple
                                    size="small"
                                />
                            </ListItemIcon>
                            <ListItem disablePadding value={option}>
                                <Stack direction="column">
                                    {option.name}
                                    <Stack direction="row">
                                        <DateText>
                                            {format(new Date(option.startDate), "d MMM y")} -{" "}
                                            {format(new Date(option.endDate), "d MMM y")}
                                        </DateText>
                                    </Stack>
                                </Stack>
                            </ListItem>
                        </ListItemButton>
                    ))}
                </List>
            </Box>
            <Button
                variant="contained"
                size="small"
                disabled={pendingSelection.length === 0}
                onClick={applySelection}
                sx={{ alignSelf: "flex-end", mt: 1 }}
            >
                Apply ({pendingSelection.length})
            </Button>
        </Stack>
    );
};

export default SprintPickerModal;
